import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { fetchEasykitDriverTasksById } from '@/api/easykit';
import { compareCellOrder, isMarkerTask, TYPE_PILL } from '@/utils/easykit';
import type { EasykitDriverTask } from '@/types/odoo';

const DRIVER_NAMES: Record<number, string> = {
  37732: 'Dries Vercruyssen',
  37735: 'Guy Berckmans',
  37738: 'Olivier Coosemans',
};

const DUTCH_DAYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const DUTCH_MONTHS = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const TAG_CONFIG: Record<number, { label: string; cls: string }> = {
  7: { label: 'Voormiddag', cls: 'bg-green-100 text-green-800' },
  6: { label: 'Namiddag', cls: 'bg-blue-100 text-blue-800' },
  12: { label: 'Afhaling', cls: 'bg-amber-100 text-amber-800' },
};

const TYPE_COLORS: Record<string, { indicator: string; bg: string; border: string }> = {
  druktest: { indicator: 'bg-red-500', bg: 'bg-red-50', border: 'border-l-red-500' },
  levering: { indicator: 'bg-blue-500', bg: 'bg-blue-50', border: 'border-l-blue-500' },
  feestdag: { indicator: 'bg-purple-500', bg: 'bg-purple-50', border: 'border-l-purple-500' },
  other: { indicator: 'bg-gray-400', bg: 'bg-gray-50', border: 'border-l-gray-400' },
};

function getPrimaryLabel(task: EasykitDriverTask): string {
  if (task.taskType === 'druktest' && task.soSummary) {
    const n = task.soSummary.collectors;
    return `${n} ${n === 1 ? 'collector' : 'collectoren'}`;
  }
  return task.soSummary?.headline ?? task.name;
}

function formatDateLabel(date: Date): string {
  const dayLabel = DUTCH_DAYS[date.getDay()];
  return `${dayLabel} ${date.getDate()} ${DUTCH_MONTHS[date.getMonth()]}`;
}

function MirrorTaskCard({ task, index }: { task: EasykitDriverTask; index: number }) {
  const type = task.taskType;
  const color = TYPE_COLORS[type] || TYPE_COLORS.other;
  const pill = TYPE_PILL[type];
  const primaryLabel = getPrimaryLabel(task);

  return (
    <div className={`rounded-xl border border-gray-200 ${color.bg} border-l-4 ${color.border} p-4`}>
      <div className="flex items-start gap-3">
        <span className="text-xs font-bold text-gray-400 mt-0.5 w-5 shrink-0">{index}</span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {pill && (
              <span className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${pill.cls}`}>
                {pill.label}
              </span>
            )}
            {task.prefix && (
              <span className="shrink-0 text-[11px] font-bold text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded truncate max-w-[60%]">
                {task.prefix}
              </span>
            )}
            <p className="text-sm font-bold text-td-navy">{primaryLabel}</p>
          </div>
          {task.projectName && (
            <p className="text-sm text-td-navy/70 mt-0.5">{task.projectName}</p>
          )}
          {task.address && (
            <p className="text-xs text-td-muted mt-1 flex items-center gap-1">
              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {task.address}
            </p>
          )}
          {task.tagIds && task.tagIds.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {task.tagIds.map(id => {
                const cfg = TAG_CONFIG[id];
                if (!cfg) return null;
                return <span key={id} className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${cfg.cls}`}>{cfg.label}</span>;
              })}
            </div>
          )}
          {task.description && (
            <p className="text-xs text-td-muted mt-2 whitespace-pre-wrap">{task.description}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function MarkerTaskCard({ task }: { task: EasykitDriverTask }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50 border-l-4 border-l-gray-300 px-4 py-2">
      <p className="text-xs font-medium text-gray-400">{task.name}</p>
    </div>
  );
}

export function EasykitDriverMirrorView() {
  const { driverId: driverIdParam } = useParams<{ driverId: string }>();
  const driverId = Number(driverIdParam);
  const driverName = DRIVER_NAMES[driverId] || `Chauffeur ${driverId}`;

  const [tasks, setTasks] = useState<EasykitDriverTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dayOffset, setDayOffset] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchEasykitDriverTasksById(driverId);
      setTasks(data);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Calculate target date (use local date, not UTC, to avoid timezone shift)
  const targetDate = new Date();
  targetDate.setHours(0, 0, 0, 0);
  targetDate.setDate(targetDate.getDate() + dayOffset);
  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getDate()).padStart(2, '0');
  const targetDateKey = `${y}-${m}-${d}`;

  const dateLabel = formatDateLabel(targetDate);
  const isToday = dayOffset === 0;

  // Filter tasks for selected day
  const dayTasks = tasks
    .filter((t) => t.dateDeadline.slice(0, 10) === targetDateKey)
    // Same cell order as the planning grid (sequence, then id) so the printed
    // day matches the order the projectleider arranged — see compareCellOrder.
    .sort(compareCellOrder);

  const realTasks = dayTasks.filter((t) => !isMarkerTask(t.name));
  const markerTasks = dayTasks.filter((t) => isMarkerTask(t.name));

  const handlePrint = () => {
    const overlay = document.createElement('div');
    overlay.id = 'driver-print-overlay';
    const esc = (s: string) => s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    overlay.innerHTML = `
      <style>
        #driver-print-overlay { display: none; }
        @media print {
          body > *:not(#driver-print-overlay) { display: none !important; }
          #driver-print-overlay {
            display: block !important;
            font-family: system-ui, -apple-system, sans-serif;
            font-size: 12px;
            padding: 0;
            color: #000;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          .dp-title-bar {
            background: #0d7377;
            color: #fff;
            padding: 12px 20px 8px;
            font-size: 20px;
            font-weight: 700;
          }
          .dp-subtitle {
            font-size: 11px;
            font-weight: 400;
            color: rgba(255,255,255,0.7);
            margin-top: 2px;
          }
          .dp-legend {
            display: flex;
            gap: 20px;
            padding: 10px 20px;
            font-size: 11px;
            border-bottom: 1px solid #e5e7eb;
          }
          .dp-legend-item { display: flex; align-items: center; gap: 6px; }
          .dp-dot { width: 10px; height: 10px; border-radius: 2px; }
          .dp-dot-dt { background: #dc2626; }
          .dp-dot-lev { background: #2563eb; }
          .dp-dot-other { background: #9ca3af; }
          .dp-table { width: 100%; border-collapse: collapse; margin: 0; }
          .dp-table th {
            background: #1e293b;
            color: #fff;
            text-align: left;
            padding: 8px 12px;
            font-size: 11px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .dp-table td {
            padding: 10px 12px;
            vertical-align: top;
            border-bottom: 1px solid #e5e7eb;
          }
          .dp-table tr:nth-child(even) td { background: #f8fafc; }
          .dp-row-dt td:first-child { border-left: 4px solid #dc2626; }
          .dp-row-lev td:first-child { border-left: 4px solid #2563eb; }
          .dp-row-other td:first-child { border-left: 4px solid #9ca3af; }
          .dp-row-dt { background: #fef2f2 !important; }
          .dp-row-lev { background: #eff6ff !important; }
          .dp-num { font-weight: 700; color: #dc2626; font-size: 13px; }
          .dp-num-lev { color: #2563eb; }
          .dp-num-other { color: #6b7280; }
          .dp-task-name { font-weight: 700; font-size: 13px; color: #111; }
          .dp-prefix { background: #fef3c7; color: #92400e; padding: 1px 4px; border-radius: 3px; font-size: 11px; margin-right: 4px; }
          .dp-project { color: #0d9488; font-size: 11px; margin-top: 1px; }
          .dp-description { color: #555; font-size: 10px; margin-top: 3px; white-space: pre-wrap; font-style: italic; }
          .dp-tag { display: inline-block; padding: 1px 8px; border-radius: 10px; font-size: 9px; font-weight: 600; margin-right: 4px; margin-top: 3px; }
          .dp-tag-vm { background: #dcfce7; color: #166534; }
          .dp-tag-nm { background: #dbeafe; color: #1e40af; }
          .dp-tag-af { background: #fef3c7; color: #92400e; }
        }
      </style>
      <div class="dp-title-bar">
        ${esc(driverName)} \u2014 ${dateLabel.toUpperCase()}
        <div class="dp-subtitle">Afgedrukt op ${new Date().toLocaleDateString('nl-BE')}, ${new Date().toLocaleTimeString('nl-BE')}</div>
      </div>
      <div class="dp-legend">
        <span class="dp-legend-item"><span class="dp-dot dp-dot-dt"></span> Druktest</span>
        <span class="dp-legend-item"><span class="dp-dot dp-dot-lev"></span> Levering</span>
        <span class="dp-legend-item"><span class="dp-dot dp-dot-other"></span> Overige</span>
      </div>
      <table class="dp-table">
        <thead><tr><th>#</th><th>TAAK</th><th>ADRES</th></tr></thead>
        <tbody>
          ${realTasks
            .map((t, i) => {
              const type = t.taskType;
              const rowClass = type === 'druktest' ? 'dp-row-dt' : type === 'levering' ? 'dp-row-lev' : 'dp-row-other';
              const numClass = type === 'druktest' ? 'dp-num' : type === 'levering' ? 'dp-num dp-num-lev' : 'dp-num dp-num-other';
              const descHtml = t.description ? `<div class="dp-description">${esc(t.description)}</div>` : '';
              const prefixHtml = t.prefix ? `<span class="dp-prefix">${esc(t.prefix)}</span> ` : '';
              const tags = (t.tagIds || []).map((id: number) => {
                if (id === 7) return '<span class="dp-tag dp-tag-vm">Voormiddag</span>';
                if (id === 6) return '<span class="dp-tag dp-tag-nm">Namiddag</span>';
                if (id === 12) return '<span class="dp-tag dp-tag-af">Afhaling</span>';
                return '';
              }).filter(Boolean).join('');
              const printLabel = getPrimaryLabel(t);
              const typePrefix = type === 'druktest' ? 'DT ' : type === 'levering' ? 'LEV ' : '';
              return `
              <tr class="${rowClass}">
                <td><span class="${numClass}">${i + 1}</span></td>
                <td>
                  <div class="dp-task-name">${prefixHtml}${esc(typePrefix + printLabel)}</div>
                  <div class="dp-project">${esc(t.projectName || '')}</div>
                  ${tags ? `<div>${tags}</div>` : ''}
                  ${descHtml}
                </td>
                <td>${esc(t.address || '\u2014')}</td>
              </tr>
            `;
            })
            .join('')}
        </tbody>
      </table>
    `;
    // Remove any existing overlay first
    document.getElementById('driver-print-overlay')?.remove();
    document.body.appendChild(overlay);
    setTimeout(() => {
      window.print();
      // Clean up immediately after print dialog closes
      overlay.remove();
    }, 300);
  };

  return (
    <div className="px-4 lg:px-8 py-5 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-1 gap-2">
        <h1 className="text-xl font-extrabold text-td-navy truncate min-w-0 flex-1" title={driverName}>{driverName}</h1>
        <div className="flex items-center gap-2 shrink-0">
          {/* Print */}
          <button
            type="button"
            onClick={handlePrint}
            disabled={realTasks.length === 0}
            className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-30 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Afdrukken"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          {/* Refresh */}
          <button
            type="button"
            onClick={() => fetchTasks()}
            disabled={loading}
            className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Vernieuwen"
          >
            <svg
              className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Last refreshed */}
      {lastRefreshed && (
        <p className="text-[11px] text-td-muted mb-4">
          Laatst vernieuwd: {lastRefreshed.toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' })}
        </p>
      )}

      {/* Day navigation */}
      <div className="flex items-center justify-between mb-5 bg-white rounded-xl border border-gray-200 p-3">
        <button
          type="button"
          onClick={() => setDayOffset(dayOffset - 1)}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Vorige dag"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="text-center">
          <h2 className="text-lg font-bold text-td-navy">
            {isToday ? 'Vandaag' : dayOffset === 1 ? 'Morgen' : dateLabel}
          </h2>
          <p className="text-xs text-td-muted">{targetDateKey}</p>
        </div>

        <button
          type="button"
          onClick={() => setDayOffset(dayOffset + 1)}
          className="p-2 rounded-lg text-td-muted hover:bg-gray-100 active:bg-gray-200 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          aria-label="Volgende dag"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Today button */}
      {!isToday && (
        <div className="flex justify-center mb-4">
          <button
            type="button"
            onClick={() => setDayOffset(0)}
            className="text-xs font-semibold text-td-blue hover:text-td-blue/80 transition-colors"
          >
            Naar vandaag
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && tasks.length === 0 && (
        <div className="space-y-3">
          {[1, 2, 3].map((n) => (
            <div key={n} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-1/2 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 text-red-600 p-4 rounded-xl" role="alert">
          <p className="font-semibold">Fout bij laden</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            type="button"
            onClick={() => fetchTasks()}
            className="mt-3 text-sm font-bold text-red-700 hover:text-red-800 underline min-h-[48px]"
          >
            Opnieuw proberen
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && dayTasks.length === 0 && (
        <div className="text-center py-16">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-td-muted text-sm font-medium">Geen taken voor deze dag</p>
        </div>
      )}

      {/* Task count */}
      {!loading && !error && realTasks.length > 0 && (
        <p className="text-xs font-semibold text-td-muted mb-3">
          {realTasks.length} {realTasks.length === 1 ? 'taak' : 'taken'}
        </p>
      )}

      {/* Real tasks */}
      {!error && realTasks.length > 0 && (
        <div className="space-y-2">
          {realTasks.map((task, i) => (
            <MirrorTaskCard key={task.id} task={task} index={i + 1} />
          ))}
        </div>
      )}

      {/* Marker tasks */}
      {!error && markerTasks.length > 0 && (
        <div className="space-y-1 mt-3">
          {markerTasks.map((task) => (
            <MarkerTaskCard key={task.id} task={task} />
          ))}
        </div>
      )}
    </div>
  );
}
